import React, { useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Notification sound (Web Audio API) ───
const playNotifSound = (type = "alert") => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    if (type === "critical") {
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.15);
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.frequency.setValueAtTime(880, ctx.currentTime + 0.2);
      gain2.gain.setValueAtTime(0.3, ctx.currentTime + 0.2);
      gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
      osc2.start(ctx.currentTime + 0.2);
      osc2.stop(ctx.currentTime + 0.35);
    } else {
      osc.frequency.setValueAtTime(587, ctx.currentTime);
      osc.frequency.setValueAtTime(784, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.4);
    }
  } catch { /* audio unavailable — silent */ }
};

// ═══════════════════════════════════════════════════════════════
// DoozyBot Notification Provider — routes critical events to clean
// `sonner` toasts (no more full-screen flash or custom stacking popups).
// ═══════════════════════════════════════════════════════════════
const DoozyBotNotifications = ({ authAxios, soundEnabled = true }) => {
  const seenIdsRef = useRef(new Set());

  const fire = useCallback(({ severity, title, detail }) => {
    const opts = { description: detail || undefined, duration: severity === "high" ? 9000 : 6000 };
    if (severity === "high") toast.error(title, opts);
    else if (severity === "medium") toast.warning(title, opts);
    else toast.info(title, opts);
    if (soundEnabled) playNotifSound(severity === "high" ? "critical" : "alert");
  }, [soundEnabled]);

  useEffect(() => {
    if (!authAxios) return;

    const poll = async () => {
      try {
        const r = await authAxios.get(`${API_URL}/api/doozybot/notifications`);
        const notifs = r.data.notifications || [];
        for (const n of notifs) {
          const nid = n.notification_id;
          if (seenIdsRef.current.has(nid)) continue;
          seenIdsRef.current.add(nid);
          const alerts = n.alerts || [];
          if (alerts.length > 0) {
            fire({
              severity: "high",
              title: `${n.high_count || alerts.length} Critical Alert${(n.high_count || alerts.length) > 1 ? "s" : ""}`,
              detail: alerts[0]?.title || "Action required",
            });
          } else {
            fire({ severity: "medium", title: "DoozyBot Scan Complete", detail: `${n.high_count || 0} issues found` });
          }
        }
        if (notifs.length > 0) await authAxios.post(`${API_URL}/api/doozybot/notifications/mark-read`);
      } catch { /* silent */ }

      try {
        const sr = await authAxios.get(`${API_URL}/api/admin/staff-events/unread`);
        const events = sr.data?.items || [];
        for (const ev of events) {
          const eid = `staff_${ev.event_id}`;
          if (seenIdsRef.current.has(eid)) continue;
          seenIdsRef.current.add(eid);
          fire({ severity: ev.severity || "medium", title: ev.title || "Staff update", detail: ev.detail || "" });
        }
        if (events.length > 0) await authAxios.post(`${API_URL}/api/admin/staff-events/mark-read`);
      } catch { /* silent */ }

      try {
        const br = await authAxios.get(`${API_URL}/api/admin/booking-events/unread`);
        const bevents = br.data?.items || [];
        for (const ev of bevents) {
          const eid = `booking_${ev.event_id}`;
          if (seenIdsRef.current.has(eid)) continue;
          seenIdsRef.current.add(eid);
          fire({ severity: ev.severity || "high", title: ev.title || "New paid booking", detail: ev.detail || "" });
        }
        if (bevents.length > 0) await authAxios.post(`${API_URL}/api/admin/booking-events/mark-read`);
      } catch { /* silent */ }
    };

    poll();
    const interval = setInterval(poll, 30000);
    return () => clearInterval(interval);
  }, [authAxios, fire]);

  return null;
};

export default DoozyBotNotifications;
